import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import axiosInstance from 'axiosConfig';
import { getOfflineMeasurements } from 'hooks/useOfflineMeasurements';
import { hasPendingDeleteForMeasurement } from 'hooks/useOfflineQueue';

interface MeasurementList {
  uuid: string;
  typeOfMeasurement: string;
  isFinished: boolean;
  measurementLabel: string;
  roomName: string;
  pageCount?: number;
  lastFinishedPage?: number;
}

const getMeasurementsFromOfflineApiCache = (orderUuid: string | null) => {
  if (!orderUuid) return null;
  try {
    const raw = localStorage.getItem('offlineApiCache');
    if (!raw) return null;
    const cache = JSON.parse(raw) as Record<string, { data: any; timestamp: number; url: string }>;
    const keysToTry = [
      `measurement/list?orderUuid=${orderUuid}`,
      `/measurement/list?orderUuid=${orderUuid}`,
    ];

    for (const k of keysToTry) {
      const hit = cache?.[k]?.data;
      if (hit) return hit;
    }

    return null;
  } catch (e) {
    console.error('Error reading offlineApiCache for measurement list:', e);
    return null;
  }
};

export default function useGetMeasurements(orderUuid: string | null, options = {}) {
  const queryClient = useQueryClient();

  // Gdy w tej samej karcie zmienia się localStorage offlineMeasurements,
  // wymuszamy odświeżenie listy (event `storage` tu nie zadziała).
  useEffect(() => {
    if (!orderUuid) return;
    const handler = () => {
      // Nie polegamy na requestach sieciowych. Wystarczy wymusić przeliczenie `select`,
      // bo merge i tak zależy od localStorage (offlineMeasurements).
      // Ustawiając nową wartość w cache powodujemy rerender od razu.
      queryClient.setQueryData(['measurements', orderUuid], (prev: any) => {
        if (prev && typeof prev === 'object') {
          return { ...prev, __offlineTs: Date.now() };
        }
        return { measurements: [], __offlineTs: Date.now() };
      });

      // Dodatkowo oznaczamy jako nieaktualne (jeśli akurat jesteśmy online, to może się odświeżyć w tle).
      queryClient.invalidateQueries({ queryKey: ['measurements', orderUuid] });
    };

    window.addEventListener('offlineMeasurementsChanged', handler as any);
    return () => {
      window.removeEventListener('offlineMeasurementsChanged', handler as any);
    };
  }, [orderUuid, queryClient]);

  return useQuery({
    queryKey: ['measurements', orderUuid],
    staleTime: 0,
    queryFn: async () => {
      if (!navigator.onLine) {
        const cached = getMeasurementsFromOfflineApiCache(orderUuid);
        if (cached) return cached;
        return { measurements: [] };
      }

      try {
        const res = await axiosInstance.get(
          `/measurement/list?orderUuid=${orderUuid}`
        );
        return res.data;
      } catch (e) {
        // Offline fallback (np. Service Worker zwraca "API offline - brak danych")
        const cached = getMeasurementsFromOfflineApiCache(orderUuid);
        if (cached) return cached;
        return { measurements: [] };
      }
    },
    select: (data: {measurements: MeasurementList[]}) => {
      // React Query potrafi zwrócić cache/initialData bez wykonania queryFn.
      // Dlatego w OFFLINE dokładamy niezależny fallback na offlineApiCache także tutaj.
      const cachedOfflinePayload = !navigator.onLine
        ? (getMeasurementsFromOfflineApiCache(orderUuid) as { measurements?: MeasurementList[] } | null)
        : null;

      const serverMeasurementsRaw =
        (data?.measurements && data.measurements.length > 0)
          ? data.measurements
          : (cachedOfflinePayload?.measurements ?? []);

      const serverMeasurements = serverMeasurementsRaw.filter(
        (m) => !hasPendingDeleteForMeasurement(m.uuid)
      );
      const offlineMeasurementsRaw = getOfflineMeasurements(orderUuid);
      const offlineMeasurements = offlineMeasurementsRaw.map((m) => ({
        uuid: m.uuid,
        typeOfMeasurement: m.typeOfMeasurement,
        isFinished: Boolean(m.isFinished),
        measurementLabel: m.measurementLabel,
        roomName: m.roomName || '',
        pageCount: m.pageCount,
        lastFinishedPage: m.lastFinishedPage,
      }));

      const merged = [
        ...serverMeasurements,
        ...offlineMeasurements.filter(
          (offline) =>
            !serverMeasurements.some((server) => server.uuid === offline.uuid)
        ),
      ];


      return merged;
    },
    retry: false,
    enabled: !!orderUuid,
    ...options,
  });
}